/**
 * Page scale and calibration math, matching Centerline for Mac
 * (`TakeoffDocument.setScale`, `PDFCanvasView.finishCalibration`,
 * `ToolSidebar.commonScales`, `LiveReadout`, `QuickMeasure`).
 */
import { compactNumber, printfFixed } from "./format.js";
import type { PageScale } from "./types.js";
import {
  formatArea, formatLength, type QuickAreaUnit, type QuickLengthUnit,
} from "./units.js";

export const POINTS_PER_INCH = 72;

export interface Point { x: number; y: number }

export interface ScalePreset { label: string; feetPerInch: number }

/** The "Common scales" menu, in the same order and wording. */
export const COMMON_SCALES: readonly ScalePreset[] = [
  { label: `Architectural 1/8" = 1'-0"`, feetPerInch: 8 },
  { label: `Architectural 1/4" = 1'-0"`, feetPerInch: 4 },
  { label: `Architectural 3/8" = 1'-0"`, feetPerInch: 8 / 3 },
  { label: `Architectural 1/2" = 1'-0"`, feetPerInch: 2 },
  { label: `Engineering 1" = 10'`, feetPerInch: 10 },
  { label: `Engineering 1" = 20'`, feetPerInch: 20 },
  { label: `Engineering 1" = 30'`, feetPerInch: 30 },
  { label: `Engineering 1" = 40'`, feetPerInch: 40 },
  { label: `Engineering 1" = 50'`, feetPerInch: 50 },
  { label: `Engineering 1" = 60'`, feetPerInch: 60 },
];

export function feetPerInch(scale: Pick<PageScale, "feetPerPoint">): number {
  return scale.feetPerPoint * POINTS_PER_INCH;
}

/** Default label for a direct/preset scale: `1" = 20'`, `1" = 2.67'`. */
export function scaleLabelForFeetPerInch(feetPerInchValue: number): string {
  return `1" = ${compactNumber(feetPerInchValue)}'`;
}

/** `setScale(pageIndex:feetPerInch:label:)`. Returns null where Swift would no-op. */
export function scaleFromFeetPerInch(feetPerInchValue: number, label?: string, source: PageScale["source"] = "direct"): PageScale | null {
  if (!(feetPerInchValue > 0)) return null;
  return {
    feetPerPoint: feetPerInchValue / POINTS_PER_INCH,
    displayLabel: label ?? scaleLabelForFeetPerInch(feetPerInchValue),
    source,
  };
}

/** Two-point calibration: a line `rawPoints` long is `knownFeet` in the field. */
export function scaleFromCalibration(a: Point, b: Point, knownFeet: number): PageScale | null {
  const rawPoints = Math.hypot(b.x - a.x, b.y - a.y);
  if (!(rawPoints > 0.1) || !(knownFeet > 0)) return null;
  return {
    feetPerPoint: knownFeet / rawPoints,
    displayLabel: `Calibrated: 1" = ${printfFixed((knownFeet * POINTS_PER_INCH) / rawPoints, 2)}'`,
    source: "calibrated",
  };
}

/** `TakeoffDocument.sameScale`: equal within 1e-9 relative. */
export function sameScale(a: Pick<PageScale, "feetPerPoint">, b: Pick<PageScale, "feetPerPoint">): boolean {
  return Math.abs(a.feetPerPoint - b.feetPerPoint) <= 1e-9 * Math.max(a.feetPerPoint, b.feetPerPoint);
}

/** Same sheet either way round, within 1% (24×36 portrait == 36×24 landscape). */
export function paperSizesMatch(a: { width: number; height: number }, b: { width: number; height: number }, tolerance = 0.01): boolean {
  const aShort = Math.min(a.width, a.height), aLong = Math.max(a.width, a.height);
  const bShort = Math.min(b.width, b.height), bLong = Math.max(b.width, b.height);
  const close = (x: number, y: number) => Math.abs(x - y) <= tolerance * Math.max(x, y, 1);
  return close(aShort, bShort) && close(aLong, bLong);
}

/** Parses "3-12, 15" (1-based) into sorted 0-based page indices; null if malformed or out of range. */
export function pageIndicesFromRangeText(text: string, pageCount: number): number[] | null {
  const tokens = text.split(/[,;]/).map((t) => t.trim()).filter((t) => t.length > 0);
  if (tokens.length === 0) return null;
  const result = new Set<number>();
  for (const token of tokens) {
    const bounds = token.split(/[-–—]/).map((t) => t.trim());
    if (bounds.length < 1 || bounds.length > 2) return null;
    const parse = (s: string) => (/^[+-]?\d+$/.test(s) ? parseInt(s, 10) : NaN);
    const first = parse(bounds[0]!), last = parse(bounds[bounds.length - 1]!);
    if (Number.isNaN(first) || Number.isNaN(last)) return null;
    const low = Math.min(first, last), high = Math.max(first, last);
    if (low < 1 || high > pageCount) return null;
    for (let i = low - 1; i <= high - 1; i++) result.add(i);
  }
  return [...result].sort((x, y) => x - y);
}

// MARK: geometry (page points)

export function pathLength(points: readonly Point[], closed: boolean): number {
  if (points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) total += Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
  if (closed && points.length >= 3) {
    const first = points[0]!, last = points[points.length - 1]!;
    total += Math.hypot(first.x - last.x, first.y - last.y);
  }
  return total;
}

/** Shoelace area of one closed ring (`Measurement.ringArea`). */
export function ringArea(ring: readonly Point[]): number {
  if (ring.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i]!, n = ring[(i + 1) % ring.length]!;
    sum += p.x * n.y - n.x * p.y;
  }
  return Math.abs(sum) / 2;
}

export function lengthFeet(points: readonly Point[], closed: boolean, feetPerPoint: number): number {
  return pathLength(points, closed) * feetPerPoint;
}

export function areaSquareFeet(points: readonly Point[], feetPerPoint: number): number {
  return ringArea(points) * feetPerPoint * feetPerPoint;
}

export const UNSCALED_TEXT = "UNSCALED — calibrate sheet";

/** `QuickMeasure.labelText`. */
export function quickMeasureLabel(
  vertices: readonly Point[],
  style: { closed: boolean; lengthUnit: QuickLengthUnit; areaUnit: QuickAreaUnit },
  feetPerPoint: number | null | undefined,
): string {
  if (feetPerPoint == null || !(feetPerPoint > 0)) return UNSCALED_TEXT;
  if (style.closed) {
    const area = formatArea(style.areaUnit, areaSquareFeet(vertices, feetPerPoint));
    const perimeter = formatLength(style.lengthUnit, lengthFeet(vertices, true, feetPerPoint));
    return `${area}\nPerimeter ${perimeter}`;
  }
  return formatLength(style.lengthUnit, lengthFeet(vertices, false, feetPerPoint));
}

/** Status-bar text for a page (`currentScaleLabel` / "NO SCALE"). */
export function statusScaleText(scale: PageScale | null | undefined): string {
  return scale ? scale.displayLabel : "NO SCALE — calibrate in Centerline";
}

/** `ScaleCheck`: measured vs stated length. */
export interface ScaleCheck { measuredFeet: number; expectedFeet: number; errorFeet: number; errorPercent: number; withinTolerance: boolean }
export function checkScale(rawPoints: number, expectedFeet: number, scale: Pick<PageScale, "feetPerPoint">): ScaleCheck | null {
  if (!(rawPoints > 0.1) || !(expectedFeet > 0)) return null;
  const measuredFeet = rawPoints * scale.feetPerPoint;
  const errorPercent = ((measuredFeet - expectedFeet) / expectedFeet) * 100;
  return { measuredFeet, expectedFeet, errorFeet: measuredFeet - expectedFeet, errorPercent, withinTolerance: Math.abs(errorPercent) <= 0.5 };
}
